import { useSyncExternalStore } from "react";

// Separate from the guided tour; the welcome has its own Sanity experiment.
// Armed only after their first consent flow succeeds, never by visiting Home.
const PREFIX = "app_welcome_video_v1:";
const fallback = new Map<string, string>();
const listeners = new Set<() => void>();

function read(patientId: string): string | null {
  if (fallback.has(patientId)) return fallback.get(patientId)!;
  try {
    return window.localStorage.getItem(PREFIX + patientId);
  } catch {
    return fallback.get(patientId) ?? null;
  }
}

function write(patientId: string, value: "pending" | "done") {
  try {
    window.localStorage.setItem(PREFIX + patientId, value);
    fallback.delete(patientId);
  } catch {
    // Still show/finish normally in this session when storage is unavailable.
    fallback.set(patientId, value);
  }
  listeners.forEach((listener) => listener());
}

export function markWelcomeVideoPending(patientId: string) {
  if (read(patientId) !== "done") write(patientId, "pending");
}

export function markWelcomeVideoDone(patientId: string) {
  write(patientId, "done");
}

export function isWelcomeVideoPending(patientId: string): boolean {
  return read(patientId) === "pending";
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  const onStorage = (event: StorageEvent) => {
    if (event.key === null) fallback.clear();
    else if (event.key.startsWith(PREFIX))
      fallback.delete(event.key.slice(PREFIX.length));
    if (event.key === null || event.key.startsWith(PREFIX)) listener();
  };
  window.addEventListener("storage", onStorage);
  return () => {
    listeners.delete(listener);
    window.removeEventListener("storage", onStorage);
  };
}

export function useWelcomeVideoPending(patientId: string | undefined): boolean {
  return useSyncExternalStore(
    subscribe,
    () => Boolean(patientId && isWelcomeVideoPending(patientId)),
    () => false,
  );
}
